/**
 * Authentication APIs — MangoTV
 * Pattern: mirrors glotv authApis.js (Client API v6.5.1)
 *
 * V2 endpoints (recommended):
 *   POST /api/client/v2/global/login   — login with enhanced device info
 *   POST /api/client/v2/global/refresh — refresh access token
 *
 * V1 endpoints (legacy fallback):
 *   POST /api/client/v1/global/login
 *   POST /api/client/v1/global/refresh
 *
 * Common:
 *   POST /api/client/v1/global/logout
 */

import axios from "axios";
import { getClientApiUrl } from "../config/apiConfig";

/**
 * Get axios instance with current Client API base URL
 */
const getApiClient = () => {
  return axios.create({
    baseURL: `${getClientApiUrl()}/api/client`,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

// ==============================================================================
// AUTH V2 — RECOMMENDED
// ==============================================================================

/**
 * Login with V2 endpoint
 *
 * @param {Object} credentials
 * @param {string} credentials.username — "user@9mobile"
 * @param {string} credentials.password
 * @param {string} credentials.device — unique device ID
 * @param {string} credentials.device_class — MOBILE | TABLET | DESKTOP
 * @param {string} [credentials.login_type="Credentials"]
 * @param {string} [credentials.device_type] — browser/model
 * @param {string} [credentials.device_os] — ANDROID | IOS | WINDOWS | MACOS | LINUX
 * @param {string} [credentials.device_mac_1]
 * @param {Object} [credentials.device_metadata]
 *
 * @returns {Promise<Object>} — { status: "ok", data: { access_token, refresh_token, ... } }
 */
export const loginV2 = async (credentials) => {
  const apiClient = getApiClient();
  const response = await apiClient.post("/v2/global/login", {
    login_type: credentials.login_type || "Credentials",
    username: credentials.username,
    password: credentials.password,
    pin: credentials.pin,
    device: credentials.device,
    device_class: credentials.device_class,
    device_type: credentials.device_type,
    device_mac_1: credentials.device_mac_1,
    device_mac_2: credentials.device_mac_2,
    device_os: credentials.device_os,
    device_metadata: credentials.device_metadata,
    serial_no: credentials.serial_no,
    id_token: credentials.id_token,
    otp_code: credentials.otp_code,
  });
  return response.data;
};

/**
 * Refresh access token with V2 endpoint
 */
export const refreshTokenV2 = async (refreshToken) => {
  const apiClient = getApiClient();
  const response = await apiClient.post("/v2/global/refresh", {
    refresh_token: refreshToken,
  });
  return response.data;
};

// ==============================================================================
// AUTH V1 — LEGACY FALLBACK
// ==============================================================================

/**
 * Login with V1 endpoint (fallback if V2 fails)
 */
export const loginV1 = async (credentials) => {
  const apiClient = getApiClient();
  const response = await apiClient.post("/v1/global/login", {
    username: credentials.username,
    password: credentials.password,
    device: credentials.device,
    device_class: credentials.device_class,
    device_type: credentials.device_type,
    device_mac_1: credentials.device_mac_1,
    device_mac_2: credentials.device_mac_2,
    device_os: credentials.device_os,
    device_metadata: credentials.device_metadata,
  });
  return response.data;
};

/**
 * Refresh access token with V1 endpoint (fallback)
 */
export const refreshTokenV1 = async (refreshToken) => {
  const apiClient = getApiClient();
  const response = await apiClient.post("/v1/global/refresh", {
    refresh_token: refreshToken,
  });
  return response.data;
};

// ==============================================================================
// COMMON
// ==============================================================================

/**
 * Logout (clears server-side session)
 */
export const logout = async (accessToken) => {
  const apiClient = getApiClient();
  const response = await apiClient.post(
    "/v1/global/logout",
    {},
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

// ==============================================================================
// TOKEN STORAGE UTILITIES
// ==============================================================================

/**
 * Store auth tokens in localStorage after login/refresh
 *
 * V2 Response shape:
 * {
 *   token_type: "Bearer",
 *   access_token: "eyJ...",
 *   expires_in: 1800000,
 *   refresh_token: "vjb...",
 *   operator_uid: "9mobile",
 *   operator_name: "9MOBILE",
 *   user_id: 1,
 *   device_id: 1,
 *   is_multicast_network: false,
 *   is_blocked: false,
 *   subscriber_tags: []
 * }
 */
export const storeAuthTokens = (authData) => {
  try {
    localStorage.setItem("mango_access_token", authData.access_token);
    localStorage.setItem("mango_refresh_token", authData.refresh_token);
    localStorage.setItem("mango_token_type", authData.token_type);
    localStorage.setItem("mango_expires_in", authData.expires_in);
    localStorage.setItem("mango_operator_uid", authData.operator_uid);
    localStorage.setItem("mango_operator_name", authData.operator_name || "");
    localStorage.setItem("mango_user_id", authData.user_id);
    localStorage.setItem("mango_device_id_server", authData.device_id || "");
    localStorage.setItem("mango_is_blocked", authData.is_blocked);
    localStorage.setItem(
      "mango_subscriber_tags",
      JSON.stringify(authData.subscriber_tags || [])
    );
    // Store token expiry timestamp
    const expiryTime = Date.now() + authData.expires_in;
    localStorage.setItem("mango_token_expiry", expiryTime);
  } catch (error) {
    console.error("storeAuthTokens error:", error);
  }
};

/**
 * Clear all auth tokens from localStorage
 */
export const clearAuthTokens = () => {
  const keys = [
    "mango_access_token",
    "mango_refresh_token",
    "mango_token_type",
    "mango_expires_in",
    "mango_operator_uid",
    "mango_operator_name",
    "mango_user_id",
    "mango_device_id_server",
    "mango_is_blocked",
    "mango_subscriber_tags",
    "mango_token_expiry",
    "mango_authenticated",
  ];
  keys.forEach((key) => localStorage.removeItem(key));
};

export const getAccessToken = () => localStorage.getItem("mango_access_token");
export const getRefreshToken = () =>
  localStorage.getItem("mango_refresh_token");

export const isTokenExpired = (bufferTime = 5 * 60 * 1000) => {
  const expiryTime = localStorage.getItem("mango_token_expiry");
  if (!expiryTime) return true;
  return Date.now() + bufferTime >= parseInt(expiryTime, 10);
};

/**
 * Auto-refresh token if expired
 */
export const autoRefreshToken = async () => {
  if (isTokenExpired()) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const response = await refreshTokenV2(refreshToken);
      storeAuthTokens(response.data);
      return true;
    } catch {
      clearAuthTokens();
      return false;
    }
  }
  return false;
};

const authApis = {
  loginV2,
  loginV1,
  refreshTokenV2,
  refreshTokenV1,
  logout,
  storeAuthTokens,
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
  autoRefreshToken,
};

export default authApis;
